import {
  isDebugStoresDevtoolsEnabled,
  reduxDevtoolsConnection,
  sendDevtoolsState,
} from '@/utilities/reduxDevtools.utilities';
import type { StateCreator, StoreApi } from 'zustand';

export type ReduxDevtoolsActionContext = {
  currentActionType?: string;
};

export function toReduxDevtoolsStoreName(storeName: string) {
  return `sq${storeName}Store`;
}

export const createReduxDevtoolsMiddleware =
  <S extends object>(storeName: string, actionContext: ReduxDevtoolsActionContext) =>
  (config: StateCreator<S, [], []>): StateCreator<S, [], []> =>
  (set, get, api) => {
    const devtoolsStoreName = toReduxDevtoolsStoreName(storeName);

    const syncStoreToReduxDevtools = (actionType = 'setState') => {
      if (reduxDevtoolsConnection && isDebugStoresDevtoolsEnabled()) {
        sendDevtoolsState({ type: `${devtoolsStoreName}/${actionType}` });
      }
    };

    const trackedSet: typeof set = ((state, replace) => {
      set(state as Parameters<typeof set>[0], replace as Parameters<typeof set>[1]);
      syncStoreToReduxDevtools(actionContext.currentActionType);
    }) as typeof set;

    api.setState = ((state, replace) => {
      set(state as Parameters<typeof set>[0], replace as Parameters<typeof set>[1]);
      syncStoreToReduxDevtools(actionContext.currentActionType);
    }) as typeof api.setState;

    return config(trackedSet, get, api);
  };

export function wrapActionsWithReduxDevtools<A extends Record<string, any>, S extends object>(
  actions: A,
  storeApi: StoreApi<S>,
  storeName: string,
  actionContext: ReduxDevtoolsActionContext,
): A {
  for (const [actionName, action] of Object.entries(actions)) {
    if (typeof action !== 'function') {
      // eslint-disable-next-line no-continue
      continue;
    }

    (actions as Record<string, unknown>)[actionName] = (...args: unknown[]) => {
      actionContext.currentActionType = actionName;
      try {
        return action(...args);
      } finally {
        actionContext.currentActionType = undefined;
      }
    };
  }

  return actions;
}
